import { Badge } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import type { Column } from "types/Datatable";

/** One order as the orders lists build it for their rows. */
export type OrderListRow = {
  id: string;
  date: string;
  /** The order's own counter, as in the order page title. */
  internal_order_number: number | string;
  totalPrice: number;
  responsibleUser: string;
  /** Only the company-wide list carries it. */
  customerName?: string;
  signature: string;
  tone: "attention" | "success" | "info";
};

function SignatureBadge({ order }: { order: OrderListRow }) {
  const { t } = useTranslation();
  return <Badge tone={order.tone}>{t(order.signature)}</Badge>;
}

// Never split: "[4] 2026-10-12" must not break at the hyphens when the card is narrow.
function NumberAndDate({ order }: { order: OrderListRow }) {
  return (
    <span className="whitespace-nowrap font-medium">
      [{order.internal_order_number}] <PrettyDate dateString={order.date} />
    </span>
  );
}

const numberAndDate: Column = {
  key: "date",
  type: "custom",
  mobileView: "topLeft",
  render: (order: OrderListRow) => <NumberAndDate order={order} />,
};

// The company-wide list names the customer under the number and date, on its own line. Its top
// right stays empty, so a long name gets the card's whole width; `anywhere` also breaks a long
// word, which would otherwise run into the neighbouring text.
const numberDateAndCustomer: Column = {
  key: "date",
  type: "custom",
  mobileView: "topLeft",
  render: (order: OrderListRow) => (
    <div className="min-w-0">
      <NumberAndDate order={order} />
      {order.customerName && (
        <div className="text-sm text-gray-700 [overflow-wrap:anywhere]">
          {order.customerName}
        </div>
      )}
    </div>
  ),
};

const responsibleUser: Column = {
  key: "responsibleUser",
  mobileView: "bottomLeft",
};

/**
 * The columns of an order's card on a phone. A card has four corners, so the
 * columns of the table are regrouped to fit them; tapping the card opens the
 * order (the list passes `rowUrl`).
 *
 * The company-wide list says whose order it is under the number and date and
 * puts the signature status under the total; the list inside a customer
 * already knows the customer, so the signature status takes the top right.
 */
export function buildOrderPhoneColumns({
  showCustomer,
}: {
  showCustomer: boolean;
}): Column[] {
  if (showCustomer) {
    return [
      numberDateAndCustomer,
      responsibleUser,
      {
        key: "totalPrice",
        type: "custom",
        mobileView: "bottomRight",
        render: (order: OrderListRow) => (
          <div className="flex flex-col items-end gap-1">
            <Currency value={order.totalPrice} isSymbol isDecimal />
            <SignatureBadge order={order} />
          </div>
        ),
      },
    ];
  }
  return [
    numberAndDate,
    {
      key: "signature",
      type: "custom",
      mobileView: "topRight",
      render: (order: OrderListRow) => <SignatureBadge order={order} />,
    },
    responsibleUser,
    { key: "totalPrice", type: "currency", mobileView: "bottomRight" },
  ];
}
